import React from 'react';
import { Modal, Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { Check } from './icons';
import { SEARCH_TARGETS } from '../config/searchTarget';
import type { SearchTarget } from '../types';
import { colors, radius, screenPadding, spacing, typography } from '../theme/tokens';
import { t } from '../i18n';

export interface SearchTargetSheetProps {
  visible: boolean;
  selected: SearchTarget;
  onSelect: (value: SearchTarget) => void;
  onClose: () => void;
  testID?: string;
}

// Bottom-sheet de choix de l'objet de recherche (même esprit que BackgroundPicker).
export function SearchTargetSheet({ visible, selected, onSelect, onClose, testID }: SearchTargetSheetProps) {
  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose} accessibilityRole="button" />
      <View testID={testID} style={styles.sheet}>
        <View style={styles.handleRow}>
          <Text style={styles.title}>{t('editors.searchTarget')}</Text>
          <Pressable testID="search-target-close" accessibilityRole="button" accessibilityLabel={t('a11y.close')} onPress={onClose} hitSlop={8}>
            <Text style={styles.close}>✕</Text>
          </Pressable>
        </View>

        {SEARCH_TARGETS.map((opt) => {
          const active = opt.value === selected;
          return (
            <Pressable
              key={opt.value}
              testID={`search-target-${opt.value}`}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              onPress={() => onSelect(opt.value)}
              style={[styles.option, active && styles.optionActive]}
            >
              <Text style={[styles.optionText, active && styles.optionTextActive]}>{opt.label}</Text>
              {active ? <Check size={18} color={colors.electric} strokeWidth={3} /> : null}
            </Pressable>
          );
        })}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.35)' },
  sheet: {
    backgroundColor: colors.white,
    borderTopLeftRadius: radius.xl,
    borderTopRightRadius: radius.xl,
    padding: screenPadding,
    paddingBottom: spacing.xxl,
    gap: spacing.sm,
  },
  handleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.xs },
  title: { fontSize: typography.h2.size, fontWeight: typography.h2.weight, color: colors.grey900 },
  close: { fontSize: typography.h3.size, color: colors.grey500 },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 52,
    paddingHorizontal: spacing.lg,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.grey200,
  },
  optionActive: { borderColor: colors.electric, backgroundColor: colors.blueSoft },
  optionText: { fontSize: typography.bodyMd.size, color: colors.grey900 },
  optionTextActive: { fontWeight: '700', color: colors.electric },
});
